// Scheduler - the scheduler tick and claude-spawn pid tracking (ADR-0011 PR-6b).
//
// Top layer of the scheduler DAG: syncAndCheckClaudeScheduler drives each
// agent's tick, routing every due task through the in-session kanban todo-card
// nudge. The legacy `claude -p` oneshot was retired in ENG-6849, so there is no
// fall-back spawn here. Imports the lower layers (tasks/state/kanban-route)
// one-way. The pid-tracking helpers below are shared with the direct-chat
// spawn path.

import { createHash } from 'node:crypto';
import { homedir } from 'node:os';
import { join } from 'node:path';
import {
  syncTasksToScheduler,
  getReadyTasks,
  markTaskFired,
  loadSchedulerState,
  type SchedulerTaskInput,
} from '../../claude-scheduler.js';
import {
  addSpawn as addClaudePidSpawn,
  removeSpawn as removeClaudePidSpawn,
  type ClaudeSpawnRecord,
} from '../../claude-pid-tracker.js';
import { log } from '../runtime.js';
import { agentState } from '../agent-state.js';
import { resolveModelChain } from '../model.js';
import {
  formatBoardForPrompt,
  PLAN_TEMPLATES,
  KANBAN_WORK_TEMPLATES,
  BOARD_INJECT_TEMPLATES,
} from '../kanban/parsers.js';
import { buildSchedulerTaskInput } from './tasks.js';
import {
  inFlightClaudeTasks,
  claudeTaskConcurrency,
  MAX_CLAUDE_CONCURRENCY,
  claudeSchedulerStates,
} from './state.js';
import { fireScheduledTaskViaKanban } from './kanban-route.js';


// ENG-5865 — host-wide PID file recording every live claude -p spawn the
// manager owns. Since ENG-6347 retired the scheduled-task (ENG-6849) and
// direct-chat (ENG-6850) one-shots, the conversation-eval backend is the
// sole tracked spawn (ENG-6851). Persisted to disk via atomicWriteFileSync
// so a torn read is impossible. The shutdown path SIGTERMs live PIDs from
// this file; the boot path SIGKILLs any survivors.
export function claudePidFilePath(): string {
  return join(homedir(), '.augmented', 'manager-claude-pids.json');
}

/** In-memory pid → record map so shutdown doesn't have to re-read the file. */
export const inFlightClaudePids = new Map<number, ClaudeSpawnRecord>();

/** Record a fresh spawn, both in memory and on disk. */
export function registerClaudeSpawn(record: ClaudeSpawnRecord): void {
  inFlightClaudePids.set(record.pid, record);
  try { addClaudePidSpawn(claudePidFilePath(), record); }
  catch (err) { log(`[drain] pid-file write failed: ${(err as Error).message}`); }
}

/** Drop a finished spawn from both stores. Safe to call on a race. */
export function unregisterClaudeSpawn(pid: number): void {
  inFlightClaudePids.delete(pid);
  try { removeClaudePidSpawn(claudePidFilePath(), pid); }
  catch { /* non-fatal */ }
}


export async function syncAndCheckClaudeScheduler(
  agent: { agent_id: string; code_name: string; display_name: string },
  tasks: Array<Record<string, unknown>>,
  // ENG-5769: source_integration / source_external_id flow through so the
  // kanban-work nudge can render thread coordinates.
  boardItems: Array<{ id: string; title: string; status: string; priority: number; estimated_minutes?: number; deliverable?: string; result?: string; notify_channel?: string; notify_to?: string; source_integration?: string | null; source_external_id?: string | null; source_url?: string | null }>,
  refreshData: Record<string, unknown>,
): Promise<void> {
  const codeName = agent.code_name;

  // Hash-based change detection (same as OpenClaw path)
  const stableTasksHash = createHash('sha256')
    .update(JSON.stringify(tasks))
    .digest('hex')
    .slice(0, 16);

  const boardHash = boardItems.length > 0
    ? createHash('sha256')
        .update(JSON.stringify(boardItems.map((b) => ({ id: b.id, title: b.title, status: b.status, priority: b.priority, deliverable: b.deliverable }))))
        .digest('hex')
        .slice(0, 16)
    : 'empty';

  const resolvedModels = resolveModelChain(refreshData);
  const modelsHash = createHash('sha256')
    .update(JSON.stringify(resolvedModels))
    .digest('hex')
    .slice(0, 16);

  const combinedHash = `${stableTasksHash}:${boardHash}:${modelsHash}`;
  const prevHash = agentState.knownTasksHashes.get(agent.agent_id);

  if (combinedHash !== prevHash) {
    // Sync tasks to scheduler state
    const taskInputs: SchedulerTaskInput[] = tasks.map((t) => buildSchedulerTaskInput(t));

    const state = syncTasksToScheduler(codeName, agent.agent_id, taskInputs);
    claudeSchedulerStates.set(codeName, state);
    agentState.knownTasksHashes.set(agent.agent_id, combinedHash);
    log(`[claude-scheduler] Tasks synced for '${codeName}' (${taskInputs.length} task(s))`);
  }

  // Load state if not cached
  if (!claudeSchedulerStates.has(codeName)) {
    claudeSchedulerStates.set(codeName, loadSchedulerState(codeName));
  }

  const state = claudeSchedulerStates.get(codeName)!;
  const ready = getReadyTasks(state, inFlightClaudeTasks);
  if (ready.length === 0) return;

  for (const task of ready) {
    if ((claudeTaskConcurrency.get(codeName) ?? 0) >= MAX_CLAUDE_CONCURRENCY) break;

    // ENG-5662: kanban-work is never fired by the scheduler anymore (the
    // hybrid edge-trigger owns it, and the task list is filtered to drop
    // kanban-work rows) — defensive skip for any that slip through.
    if (KANBAN_WORK_TEMPLATES.has(task.templateId)) {
      const updated = markTaskFired(codeName, task.taskId, 'ok');
      claudeSchedulerStates.set(codeName, updated);
      continue;
    }

    // Enrich prompt with board context
    let prompt = task.prompt;
    if (BOARD_INJECT_TEMPLATES.has(task.templateId) && boardItems.length > 0) {
      const template = PLAN_TEMPLATES.has(task.templateId) ? 'morning-plan' : 'follow-up';
      const boardPrefix = formatBoardForPrompt(boardItems, template);
      prompt = boardPrefix + prompt;
    }

    inFlightClaudeTasks.add(task.taskId);
    claudeTaskConcurrency.set(codeName, (claudeTaskConcurrency.get(codeName) ?? 0) + 1);

    // ENG-6849: the kanban todo-card + in-session nudge is the SOLE path for
    // scheduled tasks now - the legacy `claude -p` oneshot is retired (Anthropic
    // headless billing deprecation under Max). kanban-work is the only excluded
    // family and is skipped above (defensive skip), so every remaining template
    // routes here. Terminal: no oneshot fallback. The helper advances the
    // schedule and disables a one-off.
    // NOTE: a non-persistent ("oneshot mode") claude-code agent has no live tmux
    // to nudge, so the route surfaces as a failed card rather than silently
    // spawning claude -p; the prod fleet runs every agent persistent (ENG-6849
    // verification), so this is the intended end state.
    await fireScheduledTaskViaKanban(codeName, agent.agent_id, task, prompt);
    // No long-running subprocess on this path - release the guard.
    inFlightClaudeTasks.delete(task.taskId);
    claudeTaskConcurrency.set(codeName, Math.max(0, (claudeTaskConcurrency.get(codeName) ?? 1) - 1));
  }
}

